import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';

import type { NavTabItemT } from './MainHeader';
import { MainHeader } from './MainHeader';

export const MainHeaderWithTabs: FC = (): JSX.Element => {
  const ticketsContext = useTickets();
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useUserProfile();
  const {
    activeTab,
    setActiveTab,
    setOpenedScheduleTicketTab,
    setIsScheduleTicketList,
    openedTicketTabs,
    openedScheduleTicketTabs,
    closeTicketTab,
    closeScheduleTicketTab,
    updateSearchParamsArray,
  } = ticketsContext;

  const isViewingTicketDetail = location.pathname.startsWith(`${RoutePathsE.TICKET_DETAIL}/`);
  const isOnWipPage = location.pathname === RoutePathsE.WIP;
  const currentTicketId = isViewingTicketDetail
    ? location.pathname.replace(`${RoutePathsE.TICKET_DETAIL}/`, '')
    : null;

  const effectiveSection = useMemo((): ActiveTabTypeE => {
    if (currentTicketId) {
      const params = new URLSearchParams(location.search);
      if (params.get('section') === 'schedule') return ActiveTabTypeE.Schedule;
    }
    return activeTab;
  }, [currentTicketId, location.search, activeTab]);

  const currentOpenedTabs =
    effectiveSection === ActiveTabTypeE.Schedule ? openedScheduleTicketTabs : openedTicketTabs;

  useEffect(() => {
    if (currentTicketId && effectiveSection !== activeTab) {
      setActiveTab(effectiveSection);
      sessionStorage.setItem('activeTab', effectiveSection);
    }
  }, [currentTicketId, effectiveSection, activeTab, setActiveTab]);

  useEffect(() => {
    if (!currentTicketId) return;
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    const tabLabel = params.get('tabLabel');
    if (section !== 'schedule' || !tabLabel) return;
    if (Object.values(openedScheduleTicketTabs).includes(currentTicketId)) return;

    setOpenedScheduleTicketTab(tabLabel, currentTicketId);
    setActiveTab(ActiveTabTypeE.Schedule);
    sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);

    params.delete('section');
    params.delete('tabLabel');
    const newSearch = params.toString() ? `?${params.toString()}` : '';
    navigate(`${location.pathname}${newSearch}`, { replace: true });
  }, [
    currentTicketId,
    location.search,
    location.pathname,
    openedScheduleTicketTabs,
    setOpenedScheduleTicketTab,
    setActiveTab,
    navigate,
  ]);

  const ticketTabIndex = useMemo((): number => {
    const path = location.pathname;
    if (!path.startsWith(`${RoutePathsE.TICKET_DETAIL}/`)) return 0;
    const ticketId = path.replace(`${RoutePathsE.TICKET_DETAIL}/`, '');
    const ids = Object.values(currentOpenedTabs);
    const idx = ids.indexOf(ticketId);
    return idx === -1 ? 0 : idx + 1;
  }, [location.pathname, currentOpenedTabs]);

  const handleTicketTabClick = useCallback(
    (ticketId: string, route: string): void => {
      const cleanSearch = new URLSearchParams(location.search);
      cleanSearch.delete('section');
      cleanSearch.delete('tabLabel');
      const searchStr = cleanSearch.toString();
      const targetPath = searchStr ? `${route}?${searchStr}` : route;
      if (ticketId === '' && route === RoutePathsE.TICKETS_PAGE) {
        if (effectiveSection === ActiveTabTypeE.Schedule) {
          setActiveTab(ActiveTabTypeE.Schedule);
          sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);
        } else {
          setActiveTab(ActiveTabTypeE.Tickets);
          sessionStorage.setItem('activeTab', ActiveTabTypeE.Tickets);
          setIsScheduleTicketList(false);
        }
        navigate(targetPath);
        return;
      }
      navigate(targetPath);
    },
    [effectiveSection, location.search, setActiveTab, setIsScheduleTicketList, navigate]
  );

  const handleCloseTicketTab = useCallback(
    (e: React.MouseEvent, tabLabel: string, ticketId: string): void => {
      e.preventDefault();
      e.stopPropagation();
      const path = location.pathname;
      const isOnTicketDetail = path.startsWith(`${RoutePathsE.TICKET_DETAIL}/`);
      const isClosingActiveTab = currentOpenedTabs[tabLabel] === ticketId;
      if (effectiveSection === ActiveTabTypeE.Schedule) {
        closeScheduleTicketTab(tabLabel);
      } else {
        closeTicketTab(tabLabel);
      }
      if (isOnTicketDetail && isClosingActiveTab) {
        const cleanSearch = new URLSearchParams(location.search);
        cleanSearch.delete('section');
        cleanSearch.delete('tabLabel');
        const searchStr = cleanSearch.toString();
        navigate({
          pathname: RoutePathsE.TICKETS_PAGE,
          search: searchStr ? `?${searchStr}` : '',
        });
      }
    },
    [
      location.pathname,
      location.search,
      currentOpenedTabs,
      effectiveSection,
      closeTicketTab,
      closeScheduleTicketTab,
      navigate,
    ]
  );

  const handleTabChange = useCallback(
    (tab: ActiveTabTypeE): void => {
      ticketsContext.setActiveTab(tab);
      sessionStorage.setItem('activeTab', tab);
      if (tab === ActiveTabTypeE.Schedule) {
        setIsScheduleTicketList(false);
        updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
      } else {
        setIsScheduleTicketList(false);
        updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
      }
      if (isViewingTicketDetail || isOnWipPage) {
        const cleanSearch = new URLSearchParams(location.search);
        cleanSearch.delete('section');
        cleanSearch.delete('tabLabel');
        const searchStr = cleanSearch.toString();
        navigate({
          pathname: RoutePathsE.TICKETS_PAGE,
          search: searchStr ? `?${searchStr}` : '',
        });
      }
    },
    [
      ticketsContext,
      setIsScheduleTicketList,
      updateSearchParamsArray,
      isViewingTicketDetail,
      isOnWipPage,
      location.search,
      navigate,
    ]
  );

  const handleWipTabClick = useCallback((): void => {
    navigate(RoutePathsE.WIP);
  }, [navigate]);

  const navTabs: NavTabItemT[] = useMemo(() => {
    const tabs: NavTabItemT[] = [
      {
        label: 'Tickets',
        isActive: !isOnWipPage && effectiveSection === ActiveTabTypeE.Tickets,
        onClick: (): void => handleTabChange(ActiveTabTypeE.Tickets),
      },
      {
        label: 'Schedule',
        isActive: !isOnWipPage && effectiveSection === ActiveTabTypeE.Schedule,
        onClick: (): void => handleTabChange(ActiveTabTypeE.Schedule),
      },
      {
        label: 'Insights',
        isActive: !isOnWipPage && effectiveSection === ActiveTabTypeE.Insights,
        onClick: (): void => handleTabChange(ActiveTabTypeE.Insights),
      },
    ];

    if (userProfile?.hasWipTabAccess) {
      tabs.push({
        label: 'WIP',
        isActive: isOnWipPage,
        onClick: handleWipTabClick,
      });
    }

    return tabs;
  }, [
    effectiveSection,
    handleTabChange,
    handleWipTabClick,
    isOnWipPage,
    userProfile?.hasWipTabAccess,
  ]);

  const showTicketTabsRow =
    (effectiveSection === ActiveTabTypeE.Tickets || effectiveSection === ActiveTabTypeE.Schedule) &&
    Object.keys(currentOpenedTabs).length > 0;

  return (
    <MainHeader
      navTabs={navTabs}
      openedTicketTabs={showTicketTabsRow ? currentOpenedTabs : {}}
      ticketTabIndex={ticketTabIndex}
      onTicketTabClick={showTicketTabsRow ? handleTicketTabClick : undefined}
      onCloseTicketTab={showTicketTabsRow ? handleCloseTicketTab : undefined}
    />
  );
};
